import {
  GreaterAreaDropdown,
  NeighborhoodDropdown,
  OrganizationDropdown,
  StateDropdown,
} from '@/components/dropdowns';
import { HotelImageCell, HotelStatusBadge } from '@/components/hotels';
import type { CrudConfig } from '@startupquickstart/frontend';
import { Link } from 'react-router-dom';

// Define type based on our Prisma schema
export interface Hotel {
  id: string;
  name: string;
  status: 'published' | 'draft' | 'needs_review';
  organizationId?: string;
  description?: string;
  featuredImageId?: string;
  // Location fields
  greaterAreaId?: string;
  neighborhoodId?: string;
  country: string;
  state: string;
  city: string;
  streetAddress: string;
  streetAddress_2?: string;
  postalCode: string;
  latitude?: number;
  longitude?: number;
  // Contact fields
  phoneNumber?: string;
  email?: string;
  website?: string;
  createdAt: string;
  updatedAt: string;
  // Relations for display
  organization?: {
    id: string;
    name: string;
  };
  greaterArea?: {
    id: string;
    name: string;
  };
  neighborhood?: {
    id: string;
    name: string;
  };
  featuredImage?: {
    id: string;
    filename: string;
    name: string;
    mimeType: string;
    url?: string;
  };
  // Virtual field for large image display on detail pages
  featuredImageLarge?: {
    id: string;
    filename: string;
    name: string;
    mimeType: string;
    url?: string;
  };
}

/**
 * Hotel CRUD configuration
 */
export const hotelCrudConfig: CrudConfig<Hotel> = {
  entityName: 'Hotel',
  entityNamePlural: 'Hotels',

  endpoints: {
    list: '/hotels',
    single: (id: string) => `/hotels/${id}`,
    create: '/hotels',
    update: (id: string) => `/hotels/${id}`,
    delete: (id: string) => `/hotels/${id}`,
  },

  fields: [
    {
      key: 'name',
      label: 'Name',
      type: 'text',
      required: true,
      placeholder: 'Enter hotel name',
      validation: {
        min: 2,
        max: 200,
      },
    },
    {
      key: 'status',
      label: 'Status',
      type: 'select',
      required: true,
      defaultValue: 'published',
      options: [
        { label: 'Draft', value: 'draft' },
        { label: 'Published', value: 'published' },
        { label: 'Needs Review', value: 'needs_review' },
      ],
      format: (value: string) => {
        if (!value) return null;
        return <HotelStatusBadge status={value as Hotel['status']} />;
      },
    },
    {
      key: 'organizationId',
      label: 'Organization',
      type: 'custom',
      required: false,
      placeholder: 'Select Organization',
      inputFormat: (
        value: string,
        onChange: (value: string) => void,
        field: { placeholder?: string },
      ) => (
        <OrganizationDropdown value={value} onChange={onChange} placeholder={field.placeholder} />
      ),
    },
    {
      key: 'organization',
      label: 'Organization',
      type: 'text',
      required: false,
      placeholder: 'Organization',
      disableRowClick: true,
      format: (value: { id: string; name: string } | undefined) => {
        return (
          <Link className="text-primary" to={`/organizations/${value?.id}`}>
            {value?.name || 'N/A'}
          </Link>
        );
      },
    },
    {
      key: 'description',
      label: 'Description',
      type: 'richtext',
      required: false,
      placeholder: 'Enter description',
      validation: {
        max: 2000,
      },
    },
    {
      key: 'featuredImageId',
      label: 'Featured Image',
      type: 'image',
      required: false,
      placeholder: 'Featured Image ID',
    },
    {
      key: 'featuredImage',
      label: 'Image',
      type: 'custom',
      required: false,
      format: (_value: Hotel['featuredImage'], fullRecord?: Hotel) => {
        return <HotelImageCell hotel={fullRecord} />;
      },
    },
    {
      key: 'featuredImageLarge',
      label: 'Image',
      type: 'custom',
      required: false,
      format: (_value: Hotel['featuredImage'], fullRecord?: Hotel) => {
        return <HotelImageCell hotel={fullRecord} size="lg" />;
      },
    },
    // Contact Information
    {
      key: 'phoneNumber',
      label: 'Phone Number',
      type: 'text',
      required: false,
      placeholder: 'Phone Number',
    },
    {
      key: 'email',
      label: 'Email',
      type: 'email',
      required: false,
      placeholder: 'Email Address',
    },
    {
      key: 'website',
      label: 'Website',
      type: 'text',
      required: false,
      placeholder: 'Website URL',
    },
    // Location Information
    {
      key: 'greaterAreaId',
      label: 'Greater Area',
      type: 'custom',
      required: false,
      placeholder: 'Select Greater Area',
      inputFormat: (
        value: string,
        onChange: (value: string) => void,
        field: { placeholder?: string },
      ) => (
        <GreaterAreaDropdown value={value} onChange={onChange} placeholder={field.placeholder} />
      ),
      onFieldChange: (
        _value: unknown,
        setFieldValue: (field: keyof Hotel, value: unknown) => void,
      ) => {
        // Clear neighborhood when greater area changes
        setFieldValue('neighborhoodId', undefined);
      },
    },
    {
      key: 'greaterArea',
      label: 'Greater Area',
      type: 'text',
      required: false,
      placeholder: 'Greater Area',
      disableRowClick: true,
      format: (value: { id: string; name: string } | undefined) => {
        return (
          <Link className="text-primary" to={`/greater-areas/${value?.id}`}>
            {value?.name || 'N/A'}
          </Link>
        );
      },
    },
    {
      key: 'neighborhoodId',
      label: 'Neighborhood',
      type: 'custom',
      required: false,
      placeholder: 'Select Neighborhood',
      inputFormat: (
        value: string,
        onChange: (value: string) => void,
        field: { placeholder?: string },
        formData?: Record<string, unknown>,
      ) => {
        const greaterAreaId = formData?.greaterAreaId as string | undefined;
        return (
          <NeighborhoodDropdown
            value={value}
            onChange={onChange}
            placeholder={field.placeholder}
            greaterAreaId={greaterAreaId}
          />
        );
      },
      dependsOn: ['greaterAreaId'],
    },
    {
      key: 'neighborhood',
      label: 'Neighborhood',
      type: 'text',
      required: false,
      placeholder: 'Neighborhood',
      disableRowClick: true,
      format: (value: { id: string; name: string } | undefined) => {
        return (
          <Link className="text-primary" to={`/neighborhoods/${value?.id}`}>
            {value?.name || 'N/A'}
          </Link>
        );
      },
    },
    // Address Information
    {
      key: 'country',
      label: 'Country',
      type: 'text',
      required: true,
      defaultValue: 'US',
      placeholder: 'Country',
    },
    {
      key: 'state',
      label: 'State',
      type: 'custom',
      required: true,
      placeholder: 'Select State',
      inputFormat: (
        value: string,
        onChange: (value: string) => void,
        field: { placeholder?: string },
      ) => (
        <StateDropdown value={value} onChange={onChange} placeholder={field.placeholder} />
      ),
    },
    {
      key: 'city',
      label: 'City',
      type: 'text',
      required: true,
      placeholder: 'City',
    },
    {
      key: 'streetAddress',
      label: 'Street Address',
      type: 'text',
      required: true,
      placeholder: 'Street Address',
    },
    {
      key: 'streetAddress_2',
      label: 'Address Line 2',
      type: 'text',
      required: false,
      placeholder: 'Apartment, suite, etc.',
    },
    {
      key: 'postalCode',
      label: 'Postal Code',
      type: 'text',
      required: true,
      placeholder: 'Postal Code',
    },
    // Coordinates
    {
      key: 'latitude',
      label: 'Latitude',
      type: 'number',
      required: false,
      placeholder: 'Latitude',
    },
    {
      key: 'longitude',
      label: 'Longitude',
      type: 'number',
      required: false,
      placeholder: 'Longitude',
    },
    {
      key: 'createdAt',
      label: 'Created At',
      type: 'datetime',
      readonly: true,
    },
    {
      key: 'updatedAt',
      label: 'Updated At',
      type: 'datetime',
      readonly: true,
    },
  ],

  listView: {
    fields: ['featuredImage', 'name', 'greaterArea', 'neighborhood', 'createdAt', 'updatedAt'],
    searchableFields: ['name', 'city', 'state', 'streetAddress'],
    sortableFields: ['name', 'status', 'city', 'state', 'createdAt', 'updatedAt'],
    defaultSort: {
      field: 'name',
      direction: 'asc',
    },
    itemsPerPage: 10,
    filters: [
      {
        key: 'status',
        label: 'Status',
        type: 'select',
        options: [
          { label: 'Published', value: 'published' },
          { label: 'Draft', value: 'draft' },
          { label: 'Needs Review', value: 'needs_review' },
        ],
      },
    ],
    actions: {
      create: {
        enabled: true,
      },
      view: {
        enabled: true,
      },
      edit: {
        enabled: true,
      },
      delete: {
        enabled: true,
      },
    },
  },

  detailView: {
    sections: [
      {
        title: 'Basic Information',
        description: 'Core hotel details',
        fields: ['featuredImageLarge', 'name', 'status', 'organization', 'description'],
        defaultExpanded: true,
      },
      {
        title: 'Contact Information',
        description: 'Contact details for the hotel',
        fields: ['phoneNumber', 'email', 'website'],
        defaultExpanded: true,
      },
      {
        title: 'Location',
        description: 'Geographic and location details',
        fields: ['greaterArea', 'neighborhood'],
        defaultExpanded: true,
      },
      {
        title: 'Address Information',
        description: 'Physical address details',
        fields: ['country', 'state', 'city', 'streetAddress', 'streetAddress_2', 'postalCode'],
        defaultExpanded: true,
      },
      {
        title: 'Coordinates',
        description: 'Geographic coordinates',
        fields: ['latitude', 'longitude'],
        defaultExpanded: true,
      },
      {
        title: 'System Information',
        description: 'Timestamps and system data',
        fields: ['createdAt', 'updatedAt'],
        defaultExpanded: true,
      },
    ],
  },

  form: {
    sections: [
      {
        title: 'General',
        description: 'General Information about the Hotel',
        fields: ['featuredImageId', 'name', 'status', 'organizationId', 'description'],
        columns: 1,
      },
      {
        title: 'Contact',
        description: 'Contact Information for the Hotel',
        fields: ['phoneNumber', 'email', 'website'],
        columns: 2,
      },
      {
        title: 'Location',
        description: 'Location Information for the Hotel',
        fields: ['greaterAreaId', 'neighborhoodId'],
        columns: 2,
      },
      {
        title: 'Address',
        description: 'Address Information for the Hotel',
        fields: ['country', 'state', 'city', 'streetAddress', 'streetAddress_2', 'postalCode'],
        columns: 2,
      },
      {
        title: 'Coordinates',
        description: 'Coordinates for the Hotel',
        fields: ['latitude', 'longitude'],
        columns: 2,
      },
    ],
    validation: {
      name: {
        required: true,
        min: 2,
        max: 200,
      },
      status: {
        required: true,
      },
      country: {
        required: true,
      },
      state: {
        required: true,
      },
      city: {
        required: true,
      },
      streetAddress: {
        required: true,
      },
      postalCode: {
        required: true,
      },
    },
  },
};
